import React, { useEffect } from 'react';
import Header from '../../components/feature/Header';
import StickyActions from '../../components/feature/StickyActions';

const Datenschutz: React.FC = () => {
  useEffect(() => {
    // Add Schema.org JSON-LD data
    const schemaData = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Datenschutzerklärung - NextStep",
      "description": "Datenschutzerklärung von NextStep - Auswanderungsberatung für die VAE",
      "url": `${import.meta.env.VITE_SITE_URL || "https://nextstep-uae.com"}/datenschutz`,
      "inLanguage": "de-DE",
      "isPartOf": {
        "@type": "WebSite",
        "name": "NextStep",
        "url": import.meta.env.VITE_SITE_URL || "https://nextstep-uae.com"
      }
    };

    const schemaElement = document.getElementById('schema-org-data');
    if (schemaElement) {
      schemaElement.textContent = JSON.stringify(schemaData);
    }

    // Update page title and meta tags
    document.title = "Datenschutzerklärung - NextStep VAE Auswanderungsberatung";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Datenschutzerklärung von NextStep - Ihre Daten sind bei uns sicher. Erfahren Sie, wie wir Ihre persönlichen Daten bei der Auswanderungsberatung für die VAE schützen.');
    }

    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Datenschutz, NextStep, VAE, Auswanderung, Datenschutzerklärung');
    }
  }, []);

  const handleWhatsAppClick = () => {
    const phoneNumber = '+971585375437';
    const message = encodeURIComponent('Hallo NextStep, ich habe eine Frage zum Datenschutz. Mein Name ist: [Name]');
    window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Datenschutzerklärung
            </h1>
            <p className="text-xl text-gray-600">
              Ihre Daten sind bei NextStep sicher. Hier erfahren Sie, wie wir Ihre persönlichen Daten schützen und verwenden.
            </p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto prose prose-lg">
            
            <div className="bg-gray-50 rounded-lg p-8 mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Verantwortlicher</h2>
              <div className="text-gray-700">
                <p><strong>Next Step - F.Z.E</strong></p>
                <p>Premises Number Office - C1 - 1F - SF11308</p>
                <p>Ajman Free Zone C1 Building</p>
                <p>Ajman Free Zone</p>
                <p>Makani No. 4442612247</p>
                <p>Vereinigte Arabische Emirate</p>
                <br />
                <p><strong>Kontakt:</strong></p>
                <p>Telefon: +971 58 537 5437</p>
                <p>E-Mail: info@nextstep-uae.com</p>
                <p>WhatsApp: +971 58 537 5437</p>
              </div>
            </div>

            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Allgemeine Hinweise</h2>
                <p className="text-gray-700 leading-relaxed">
                  Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen Daten passiert, wenn Sie unsere Website besuchen. Personenbezogene Daten sind alle Daten, mit denen Sie persönlich identifiziert werden können. Ausführliche Informationen zum Thema Datenschutz entnehmen Sie unserer unter diesem Text aufgeführten Datenschutzerklärung.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Datenerfassung auf unserer Website</h2>
                
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Wer ist verantwortlich für die Datenerfassung auf dieser Website?</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Die Datenverarbeitung auf dieser Website erfolgt durch den Websitebetreiber. Dessen Kontaktdaten können Sie dem Impressum dieser Website entnehmen.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 mb-3">Wie erfassen wir Ihre Daten?</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Ihre Daten werden zum einen dadurch erhoben, dass Sie uns diese mitteilen. Hierbei kann es sich z.B. um Daten handeln, die Sie über WhatsApp oder andere Kontaktformulare eingeben.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Andere Daten werden automatisch beim Besuch der Website durch unsere IT-Systeme erfasst. Das sind vor allem technische Daten (z.B. Internetbrowser, Betriebssystem oder Uhrzeit des Seitenaufrufs).
                </p>

                <h3 className="text-xl font-semibold text-gray-900 mb-3">Wofür nutzen wir Ihre Daten?</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Ein Teil der Daten wird erhoben, um eine fehlerfreie Bereitstellung der Website zu gewährleisten. Andere Daten können zur Analyse Ihres Nutzerverhaltens verwendet werden, um unsere Beratungsleistungen zu verbessern.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">4. WhatsApp Kommunikation</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Wir nutzen WhatsApp für die direkte Kommunikation mit unseren Kunden. Wenn Sie uns über WhatsApp kontaktieren, werden folgende Daten verarbeitet:
                </p>
                <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                  <li>Ihre Telefonnummer</li>
                  <li>Ihr Name (falls angegeben)</li>
                  <li>Nachrichteninhalte</li>
                  <li>Zeitpunkt der Nachrichten</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Diese Daten werden ausschließlich zur Bearbeitung Ihrer Anfrage und zur Erbringung unserer Beratungsleistungen verwendet. Die Rechtsgrundlage ist Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung).
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Beratungsleistungen</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Im Rahmen unserer Auswanderungsberatung für die VAE verarbeiten wir folgende personenbezogene Daten:
                </p>
                <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                  <li>Name, Vorname, Anschrift</li>
                  <li>Telefonnummer und E-Mail-Adresse</li>
                  <li>Berufliche Informationen</li>
                  <li>Finanzielle Informationen (soweit für die Beratung erforderlich)</li>
                  <li>Familiäre Verhältnisse</li>
                  <li>Kopien von Ausweisdokumenten</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Diese Daten werden ausschließlich zur Erbringung unserer Beratungsleistungen und zur Abwicklung der beauftragten Services verwendet.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Ihre Rechte</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Sie haben jederzeit das Recht:
                </p>
                <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2">
                  <li>Auskunft über Ihre bei uns gespeicherten personenbezogenen Daten zu erhalten</li>
                  <li>Berichtigung unrichtiger Daten zu verlangen</li>
                  <li>Löschung Ihrer Daten zu verlangen</li>
                  <li>Einschränkung der Datenverarbeitung zu verlangen</li>
                  <li>Widerspruch gegen die Verarbeitung einzulegen</li>
                  <li>Datenübertragbarkeit zu verlangen</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Datensicherheit</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Wir verwenden geeignete technische und organisatorische Sicherheitsmaßnahmen, um Ihre Daten gegen zufällige oder vorsätzliche Manipulationen, teilweisen oder vollständigen Verlust, Zerstörung oder gegen den unbefugten Zugriff Dritter zu schützen.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Speicherdauer</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Wir speichern Ihre personenbezogenen Daten nur so lange, wie dies für die Erfüllung der Zwecke erforderlich ist, für die sie erhoben wurden, oder solange gesetzliche Aufbewahrungsfristen bestehen.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Weitergabe von Daten</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Eine Übermittlung Ihrer persönlichen Daten an Dritte zu anderen als den im Folgenden aufgeführten Zwecken findet nicht statt. Wir geben Ihre persönlichen Daten nur an Dritte weiter, wenn dies zur Erfüllung unserer Beratungsleistungen erforderlich ist (z.B. an Behörden in den VAE) oder Sie ausdrücklich eingewilligt haben.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Änderungen der Datenschutzerklärung</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Wir behalten uns vor, diese Datenschutzerklärung anzupassen, damit sie stets den aktuellen rechtlichen Anforderungen entspricht oder um Änderungen unserer Leistungen in der Datenschutzerklärung umzusetzen.
                </p>
              </div>

              <div className="bg-red-50 rounded-lg p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Fragen zum Datenschutz?</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Wenn Sie Fragen zu dieser Datenschutzerklärung haben, kontaktieren Sie uns gerne über WhatsApp.
                </p>
                <button
                  onClick={handleWhatsAppClick}
                  className="inline-flex items-center px-6 py-3 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-whatsapp-line mr-3 text-xl"></i>
                  WhatsApp Chat starten
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sticky Actions for Mobile */}
      <StickyActions />
    </div>
  );
};

export default Datenschutz;